import React from "react";
import moment from "moment";
import styled from "styled-components";
import { Table, Button, Alert, Message, Tag } from "@arco-design/web-react";
import EmptyState from "../../Components/EmptyState";
import { IconEdit, IconDelete, IconRefresh } from "@arco-design/web-react/icon";
import SenderEmailAddEditDialog from "./SenderEmailAddEditDialog";
import { useNavigate } from "react-router-dom";
import { FetchApi, SaveApi, PostApi, DeleteApi } from "../../Api/AuthenticatedApi";
import { useAuth0 } from "@auth0/auth0-react";
import DeleteSenderEmailDialog from "./DeleteSenderEmailDialog";
import DomainChoiceDialog, { needsDomainChoice } from "./DomainChoiceDialog";

// MUI -> Arco. The MUI Table is rewritten into an Arco Table columns config +
// data source; the edit/delete Links become Arco icon Buttons (shape="circle"
// type="text"); the "Add" Button keeps its icon; the MUI Alert becomes an Arco
// Alert (severity -> type, children -> content). makeStyles -> styled-components;
// theme.spacing(3) = 24px. Logic + data flow are untouched.

const SeeMore = styled.div`
  margin-top: 24px;
`;

const Hint = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: ${(p) => (p.warn ? "rgb(var(--orange-6))" : "var(--color-text-3)")};
`;

// addedMessage: what happens next for a sender just added. waitDomain is the
// domain being set up that the sender waits for, if it was added that way.
export function addedMessage(sender, address, waitDomain) {
    if (sender && sender.validation_date) {
        return `Sender added: ${address} is on your verified domain, so it's ready to use.`;
    }
    if (waitDomain) {
        return `Sender added: ${address} is ready once ${waitDomain}'s DNS records are found. Add them under Sending Domains.`;
    }
    if (sender && sender.sends_via) {
        return `Sender added. We've emailed ${address} a confirm link: click it to start sending.`;
    }
    return `Sender added. Two emails are on their way to ${address}: click the link in both to start sending from it.`;
}

// onOpenSendingDomains(domain) opens the Sending Domains section showing that
// domain's DNS records (Settings passes it).
export default function OrganizationEmails({ onOpenSendingDomains }) {
    // ----------------------------
    // Add Edit Dialog
    // ----------------------------
    const [openAddEditDialog, setOpenAddEditDialog] = React.useState(false);

    const [senderName, setSenderName] = React.useState("");
    const [senderEmail, setSenderEmail] = React.useState("");
    const [senderId, setSenderId] = React.useState("");

    const handleCloseAddEditDialog = () => {
        setSenderName("");
        setSenderEmail("");
        setSenderId("");
        setOpenAddEditDialog(false);
    }

    const handleOpenAddEditDialogForEdit = (row) => {
        setSenderName(row.name);
        setSenderEmail(row.email);
        setSenderId(row.id);
        setOpenAddEditDialog(true);
    }

    const handleOpenAddEditDialogForAdd = () => {
        setSenderName("");
        setSenderEmail("");
        setSenderId("");
        setOpenAddEditDialog(true);
    }
    const handleAddEditSenderEmail = () => {
        if (senderId) {
            saveEmail({ name: senderName, email: senderEmail, id: senderId });
        } else {
            adviseThenAdd({ name: senderName, email: senderEmail });
        }
    }

    // ----------------------------
    // Domain choice: for an address on the user's own domain, offer setting
    // up the domain instead of verifying just the address.
    // ----------------------------
    const [choice, setChoice] = React.useState(null); // { data, advice }
    const [choiceBusy, setChoiceBusy] = React.useState("");
    const [choiceError, setChoiceError] = React.useState("");

    const closeChoice = () => {
        setChoice(null);
        setChoiceBusy("");
        setChoiceError("");
    };

    function adviseThenAdd(data) {
        FetchApi(getAccessTokenSilently, "/api/settings/sending-domains/advice?email=" + encodeURIComponent(data.email),
            (advice) => {
                if (needsDomainChoice(advice)) setChoice({ data, advice });
                else createEmail(data);
            },
            () => history("/login"),
            // The advice is only a suggestion: add the sender the usual way.
            () => createEmail(data));
    }

    function addWaitingForDomain(data, domain) {
        createEmail({ ...data, wait_for_domain: true }, domain, () => {
            closeChoice();
            onOpenSendingDomains && onOpenSendingDomains(domain);
        }, () => setChoiceBusy(""));
    }

    const chooseDomain = () => {
        const { data, advice } = choice;
        setChoiceBusy("domain");
        setChoiceError("");
        if (advice.status === "pending") {
            addWaitingForDomain(data, advice.domain);
            return;
        }
        PostApi(getAccessTokenSilently, "/api/settings/sending-domains/",
            (d) => addWaitingForDomain(data, (d && d.domain) || advice.domain),
            () => history("/login"),
            (err) => {
                setChoiceBusy("");
                setChoiceError((err && err.message) || "Failed to add the domain");
            },
            { domain: advice.domain });
    };

    const chooseAddress = () => {
        setChoiceBusy("address");
        createEmail(choice.data, "", closeChoice, () => setChoiceBusy(""));
    };

    // ----------------------------
    // Delete Dialog
    // ----------------------------
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

    const handleOpenDeleteDialog = (row) => {
        setSenderName(row.name);
        setSenderEmail(row.email);
        setSenderId(row.id);
        setOpenDeleteDialog(true);
    }

    const handleCloseDeleteDialog = () => {
        setSenderId("");
        setOpenDeleteDialog(false);
    }

    const handleDeleteSenderEmail = () => {
        deleteEmail(senderId);
    }

    // ----------------------------


    const [emails, setEmails] = React.useState([]);
    // viaBrand is set when Gmail-style senders are sent "via" our domain.
    const [viaBrand, setViaBrand] = React.useState("");
    const [refreshEmails, setRefreshEmails] = React.useState(true);

    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = React.useState(false);

    let history = useNavigate();

    React.useEffect(() => {
        FetchApi(getAccessTokenSilently, "/api/config", (cfg) => setViaBrand((cfg && cfg.via_brand) || ""), () => {}, () => {});
    }, [getAccessTokenSilently]);



    React.useEffect(() => {
        (async () => {
            if (refreshEmails) {
                let url = "/api/settings/sender-emails/"
                FetchApi(getAccessTokenSilently, url,
                    (data) => {
                        setLoading(false);
                        setEmails(data);
                        setRefreshEmails(false);
                    },
                    (response) => {
                        setLoading(false);
                        history("/login");
                    }, (response) => {
                        setLoading(false);
                        Message.error("Failed to load sender emails");
                    })
            }

        })();
    }, [setEmails, setLoading, history, getAccessTokenSilently, refreshEmails])

    function saveEmail(data) {
        (async () => {
            let url = "/api/settings/sender-emails/" + data.id
            SaveApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    // setEmails(data);
                    setRefreshEmails(true);
                    handleCloseAddEditDialog();
                    Message.success("Sender email updated");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to save sender email");
                }, data);
        })();
    }

    // createEmail adds a sender. waitDomain: it was added to wait for that
    // domain. onDone / onFail run after the request, for the domain choice.
    function createEmail(data, waitDomain, onDone, onFail) {
        const address = data.email;
        (async () => {
            let url = "/api/settings/sender-emails/"
            PostApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    // setEmails(data);
                    setRefreshEmails(true);
                    handleCloseAddEditDialog();
                    Message.success({ content: addedMessage(data, address, waitDomain), duration: 8000 });
                    onDone && onDone();
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to add sender email");
                    onFail && onFail();
                }, data);
        })();
    }

    function deleteEmail(id) {
        (async () => {
            let url = "/api/settings/sender-emails/" + id;
            DeleteApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    // setEmails(data);
                    setRefreshEmails(true);
                    handleCloseDeleteDialog();
                    Message.success("Sender email deleted");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to delete sender email");
                }, { "sender_email_id": id });
        })();
    }

    // which: "ours" (our confirm link) or "amazon" (Amazon's verification email).
    function resendVerification(row, which) {
        PostApi(getAccessTokenSilently, "/api/settings/sender-emails/" + row.id + "/resend-verification?which=" + which,
            () => Message.success(which === "ours"
                ? `Our confirm link was sent again to ${row.email}`
                : `Amazon's verification email was sent again to ${row.email}`),
            () => history("/login"),
            (err) => {
                Message.error((err && err.message) || "Failed to resend the email");
                // Amazon may have been done already: the status may have changed.
                setRefreshEmails(true);
            });
    }

    const columns = [
        {
            title: "From Name",
            dataIndex: "name",
            render: (name) => <div>{name}</div>,
        },
        {
            title: "Email",
            dataIndex: "email",
            render: (email, row) => (
                <div>
                    <div>{email}</div>
                    {row.free_mail && row.sends_via && (
                        <Hint>Sent as “{row.name || email} via {viaBrand || "Contact Anchor"}”; replies come to you. For the best results, add your own domain under Sending Domains.</Hint>
                    )}
                    {row.free_mail && !row.sends_via && (
                        <Hint warn>Mail from this kind of address sent through us usually lands in spam. Add your own domain under Sending Domains.</Hint>
                    )}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "validation_date",
            render: (validation_date, row) => {
                if (validation_date && row.verified_via === "domain") {
                    return <Tag color="green">Verified (your domain)</Tag>;
                }
                if (validation_date) {
                    return <Tag color="green">Verified {moment(validation_date).format("MMM D, YYYY")}</Tag>;
                }
                if (row.waiting_for_domain) {
                    return <Tag color="orange">Waiting for {row.waiting_for_domain}'s DNS records</Tag>;
                }
                if (!row.owner_confirm_date) {
                    return <Tag color="orange">Waiting for our confirm link</Tag>;
                }
                return <Tag color="orange">Waiting for Amazon's link</Tag>;
            },
        },
        {
            title: "",
            render: (_, row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {!row.validation_date && !row.owner_confirm_date && (
                        <Button size="small" type="text" icon={<IconRefresh />} onClick={() => resendVerification(row, "ours")}>
                            {row.waiting_for_domain ? "Send our link" : "Resend our link"}
                        </Button>
                    )}
                    {!row.validation_date && !row.sends_via && (
                        <Button size="small" type="text" icon={<IconRefresh />} onClick={() => resendVerification(row, "amazon")}>
                            {row.waiting_for_domain ? "Send Amazon's link" : "Resend Amazon's link"}
                        </Button>
                    )}
                    <Button
                        shape="circle"
                        type="text"
                        icon={<IconEdit />}
                        onClick={() => handleOpenAddEditDialogForEdit(row)}
                    />
                    <Button
                        shape="circle"
                        type="text"
                        icon={<IconDelete />}
                        onClick={() => handleOpenDeleteDialog(row)}
                    />
                </div>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Alert type="info" content={"Addresses on a verified sending domain are ready at once. Any other address gets our confirm link, and usually a verification link from Amazon Web Services too; once the links are clicked it flips to Verified automatically (you can send from Verified senders only). If an email was missed, use “Resend our link” or “Resend Amazon's link”."} />

            <Table
                size="small"
                columns={columns}
                data={emails}
                rowKey="id"
                pagination={false}
                border={false}
                noDataElement={
                    <EmptyState description="No sender emails yet">
                        <Button type="primary" style={{ marginTop: 12 }} onClick={handleOpenAddEditDialogForAdd}>
                            Add your first sender email
                        </Button>
                    </EmptyState>
                }
            />

            {/* With none yet, the empty message has its own button. */}
            {emails.length > 0 && (
                <SeeMore>
                    <Button
                        type="primary"
                        icon={<IconEdit />}
                        onClick={handleOpenAddEditDialogForAdd}
                    >
                        Add
                    </Button>
                </SeeMore>
            )}

            <SenderEmailAddEditDialog
                open={openAddEditDialog}

                onSave={handleAddEditSenderEmail}
                onClose={handleCloseAddEditDialog}

                senderName={senderName}
                setSenderName={setSenderName}
                senderEmail={senderEmail}
                setSenderEmail={setSenderEmail}
                senderId={senderId}
                viaBrand={viaBrand}
            />

            <DomainChoiceDialog
                choice={choice}
                busy={choiceBusy}
                error={choiceError}
                onDomain={chooseDomain}
                onAddress={chooseAddress}
                onClose={closeChoice}
            />

            <DeleteSenderEmailDialog
                open={openDeleteDialog}

                onDelete={handleDeleteSenderEmail}
                onClose={handleCloseDeleteDialog}

                senderEmail={senderEmail}
            />

        </React.Fragment>
    );
}
